import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { Skeleton } from "@/components/ui/skeleton";
import { secondsLeft } from "@/lib/bokun-test/hold-window";
import { expireBookingCheckout } from "@/lib/bokun-hold.functions";

const StripeEmbeddedPanel = lazy(() => import("@/components/booking/StripeEmbeddedPanel"));

/**
 * Pago embebido con cuenta regresiva alineada al apartado de Bókun.
 * Al llegar a 0 el formulario se desmonta y la sesión se expira en el servidor.
 */
export function HoldCheckoutPanel(props: {
  clientSecret: string;
  payDeadline: string;
  idempotencyKey: string;
  onExpired: () => void;
}) {
  const { t } = useTranslation();
  const deadline = new Date(props.payDeadline).getTime();
  const [left, setLeft] = useState(() => secondsLeft(deadline));
  const expire = useServerFn(expireBookingCheckout);
  const fired = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => setLeft(secondsLeft(deadline)), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  useEffect(() => {
    if (left > 0 || fired.current) return;
    fired.current = true;
    void expire({ data: { idempotencyKey: props.idempotencyKey } }).catch(() => null);
    props.onExpired();
  }, [left, expire, props]);

  if (left <= 0) return null;
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <div className="space-y-3">
      <div
        role="timer"
        aria-live="off"
        className={`flex items-center gap-2 rounded-2xl border p-3 text-sm ${
          left <= 120 ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-border bg-muted/40"
        }`}
      >
        <Clock className="h-4 w-4 shrink-0" aria-hidden />
        <span>
          {t("tourV2.holdTimer", { defaultValue: "Apartamos tu lugar. Completa el pago en" })}{" "}
          <strong className="tabular-nums">
            {mm}:{ss}
          </strong>
        </span>
      </div>
      <Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <StripeEmbeddedPanel clientSecret={props.clientSecret} />
      </Suspense>
    </div>
  );
}
